import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';
import { getServiceListingTypes, serviceSearchQuery } from '../../../../util/serviceSearch';
import { IconAdd, IconArrowHead, NamedLink } from '../../../../components';

import { useLandingViewerRole } from '../../../LandingPage/LandingPageCta';
import SectionContainer from '../SectionContainer';
import { getCategoryIconUrl, orderCategoriesForDisplay } from './categoryDirectoryPresentation';

import css from './SectionCategoryDirectory.module.css';

// Canonical draft listing identifiers used to start the listing-creation wizard.
const DRAFT_ID = '00000000-0000-0000-0000-000000000000';
const EDIT_LISTING_DRAFT_PARAMS = {
  slug: 'draft',
  id: DRAFT_ID,
  type: 'new',
  tab: 'details',
};

// Service listing types for the category search links (T5, D22) — the shared helper since T15
// (src/util/serviceSearch.js); re-exported here for existing callers.
export { getServiceListingTypes };

// Build an EditListingPage task-creation query string for a top-level category.
// Preselects the customer task listing type and the clicked category. No level 3.
const topLevelTaskSearch = categoryId =>
  `?type=tasks&categoryLevel1=${encodeURIComponent(categoryId)}`;

// Build an EditListingPage task-creation query string for a direct subcategory.
// Preselects the task listing type, the parent category, and the subcategory. No level 3.
const subcategoryTaskSearch = (categoryId, subcategoryId) =>
  `?type=tasks&categoryLevel1=${encodeURIComponent(categoryId)}&categoryLevel2=${encodeURIComponent(
    subcategoryId
  )}`;

// Build a SearchPage query string for a top-level category.
// Uses pub_-prefixed filters (distinct from the task-creation params above).
export const topLevelServiceSearch = (categoryId, serviceTypes = []) =>
  serviceSearchQuery(serviceTypes, { categoryLevel1: categoryId });

// Build a SearchPage query string for a direct subcategory.
export const subcategoryServiceSearch = (categoryId, subcategoryId, serviceTypes = []) =>
  serviceSearchQuery(serviceTypes, { categoryLevel1: categoryId, categoryLevel2: subcategoryId });

// Build a SearchPage task-search query string for a category (T13, D28): tasks only, with the
// clicked category and, for a subcategory, its parent.
export const categoryTaskSearch = (categoryId, subcategoryId) =>
  `?pub_listingType=tasks&pub_categoryLevel1=${encodeURIComponent(categoryId)}${
    subcategoryId ? `&pub_categoryLevel2=${encodeURIComponent(subcategoryId)}` : ''
  }`;

// A8: below 768px every group is a collapsed accordion. Must match the CSS breakpoint.
const MOBILE_LAYOUT_QUERY = '(max-width: 767.98px)';

/**
 * True when the mobile accordion layout applies. Starts false, so the server render and the
 * first client render use the desktop markup (all names are in the HTML either way; collapsed
 * lists are hidden by CSS on mobile), then follows the media query after mount.
 */
const useIsMobileLayout = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mql = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const update = () => setIsMobile(mql.matches);
    update();
    if (mql.addEventListener) {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);
  return isMobile;
};

// Maps an explicit action variant to its CSS class (D28 levels): accent = coral, the viewer's main
// action; primary = indigo fill; secondary = outline. Set per role/action, not per action type.
const VARIANT_CLASSNAMES = {
  accent: css.actionAccent,
  primary: css.actionPrimary,
  secondary: css.actionSecondary,
};

/**
 * Build the ordered popover actions for the given user role.
 *
 * T13 (D22 amended, D28): the popover holds the viewer's actions for the category, ordered and
 * styled by relevance (coral main action, indigo fill, outline). The category search is reachable
 * both from the name link (D22, server-rendered) and from «Найти специалиста» here, by choice.
 *   - find specialist -> SearchPage with the same service search as the name link
 *   - find task       -> SearchPage, tasks in this category (findTaskSearch)
 *   - create task     -> EditListingPage with the existing taskSearch (?type=tasks&categoryLevel*)
 *   - create service  -> EditListingPage draft, NO search prop (must not carry
 *                        ?type=tasks / categoryLevel1 / categoryLevel2)
 * An unresolved role gets the customer actions (no service creation for an unknown role).
 */
const buildPopoverActions = ({ role, taskSearch, serviceSearch, findTaskSearch }) => {
  const findSpecialistAction = {
    key: 'findSpecialist',
    messageId: 'SectionCategoryDirectory.actionFindSpecialist',
    linkName: 'SearchPage',
    search: serviceSearch,
  };
  const findTaskAction = {
    key: 'findTask',
    messageId: 'SectionCategoryDirectory.actionFindTask',
    linkName: 'SearchPage',
    search: findTaskSearch,
  };
  const taskAction = {
    key: 'task',
    messageId: 'SectionCategoryDirectory.actionCreateTask',
    linkName: 'EditListingPage',
    params: EDIT_LISTING_DRAFT_PARAMS,
    search: taskSearch,
  };
  const serviceAction = {
    key: 'service',
    messageId: 'SectionCategoryDirectory.actionCreateService',
    linkName: 'EditListingPage',
    params: EDIT_LISTING_DRAFT_PARAMS,
    // No search: create-service starts a plain listing draft.
  };

  if (role === 'provider') {
    return [
      { ...serviceAction, variant: 'accent' },
      { ...findTaskAction, variant: 'primary' },
      { ...taskAction, variant: 'secondary' },
    ];
  }
  if (role === 'guest') {
    return [
      { ...findSpecialistAction, variant: 'accent' },
      { ...serviceAction, variant: 'primary' },
      { ...taskAction, variant: 'secondary' },
    ];
  }
  // Customer, or an authenticated user whose role is still unresolved.
  return [{ ...findSpecialistAction, variant: 'accent' }, { ...taskAction, variant: 'primary' }];
};

/**
 * «+» button that opens the create-actions popover of a category / subcategory (T5, D22).
 * Icon-only, so it carries its own name «Создать в категории {name}».
 */
const CategoryActionsButton = props => {
  const { name, isOpen, popoverId, buttonRef, onClick } = props;
  const intl = useIntl();
  return (
    <button
      type="button"
      className={css.actionsTrigger}
      aria-label={intl.formatMessage(
        { id: 'SectionCategoryDirectory.publishInCategory' },
        { name }
      )}
      // A1-13: a disclosure (expanded + controls), not a menu: the popover is a group of links
      aria-expanded={isOpen}
      aria-controls={popoverId}
      ref={buttonRef}
      onClick={onClick}
    >
      <IconAdd className={css.actionsTriggerIcon} />
    </button>
  );
};

/**
 * Compact action popover shown when a category/subcategory actions button is clicked.
 * Renders a role-dependent, explicitly ordered set of navigation actions.
 * All actions are real links, so normal SPA navigation unmounts the popover.
 */
const CategoryActionPopover = props => {
  const { id, label, role, taskSearch, serviceSearch, findTaskSearch } = props;
  const titleId = `${id}-title`;
  const actions = buildPopoverActions({ role, taskSearch, serviceSearch, findTaskSearch });
  return (
    <div id={id} role="group" aria-labelledby={titleId} className={css.popover}>
      <span id={titleId} className={css.popoverTitle}>
        {label}
      </span>
      {actions.map(action => (
        <NamedLink
          key={action.key}
          name={action.linkName}
          {...(action.params ? { params: action.params } : {})}
          {...(action.search ? { to: { search: action.search } } : {})}
          className={classNames(VARIANT_CLASSNAMES[action.variant], {
            // Audit marker, no styles: the coral popover button at 16px / 400 is an accepted
            // colour-contrast exception (D28), excluded from that rule like the D10 hero word.
            'delowo-exception--popover-coral': action.variant === 'accent',
          })}
        >
          <FormattedMessage id={action.messageId} />
        </NamedLink>
      ))}
    </div>
  );
};

/**
 * Custom homepage section: a lightweight directory of all top-level categories
 * and their direct subcategories. Category data is sourced from the runtime
 * configuration (config.categoryConfiguration.categories), so the tree is not
 * duplicated here.
 *
 * Routed via the sectionName "all-categories" through the SectionBuilder
 * workaround and registered in LandingPage.js as customCategoryDirectory.
 *
 * Rendering rules (T5, D22):
 *   - top-level categories and direct subcategories: the name is a link to the service search
 *     (SearchPage, pub_listingType + pub_categoryLevel1[/2]), present in the server-rendered HTML;
 *   - next to each name, a «+» button opens the action popover;
 *   - mobile: the top-level heading toggles the accordion; the «Вся категория» row inside the
 *     group is the top-level link plus its «+» button;
 *   - third-level (and deeper) children are never rendered.
 *   - no counters.
 *
 * Each popover offers the role-dependent find / create actions (SearchPage / EditListingPage).
 * See buildPopoverActions for the exact order/variants.
 */
const SectionCategoryDirectory = props => {
  const { sectionId, className } = props;

  const config = useConfiguration();
  const categories = config?.categoryConfiguration?.categories ?? [];
  const serviceTypes = getServiceListingTypes(config);

  // Role from the shared landing hook (LandingPageCta.js). Read once here (not per
  // popover) and pass the resolved role down.
  const role = useLandingViewerRole();

  // Display-only reordering by approved stable IDs (row-major TARGET grid).
  // Never mutates runtime config; does not affect ids, subcategory data, URLs,
  // or popover behavior. Unknown future IDs append in configuration order.
  const displayCategories = orderCategoriesForDisplay(categories);

  const [openKey, setOpenKey] = useState(null);
  // Tracks the «+» buttons so focus can return to the originating button on Escape.
  const triggerRefs = useRef({});

  // A8: mobile accordion. Groups start collapsed; several can be open at once.
  const isMobile = useIsMobileLayout();
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const toggleKey = key => setOpenKey(prev => (prev === key ? null : key));

  // D41: while a popover is open, a click or tap anywhere outside it closes it, and Esc closes it from
  // anywhere (focus may be on <body>: Safari does not focus a pressed button) and returns focus to the
  // «+». `click`, not the `mousedown` of the stock OutsideClickHandler: below 768px the popover is in
  // the flow, so closing it on mousedown moved the content up before the click, and the click landed
  // on whatever was then under the finger. A scroll gesture is not a click, so it never closes it.
  // Clicks on a «+» are left to its own toggle: another «+» switches to its popover (one at a time).
  useEffect(() => {
    if (!openKey || typeof document === 'undefined') {
      return undefined;
    }
    const trigger = triggerRefs.current[openKey];
    const popover = trigger ? document.getElementById(trigger.getAttribute('aria-controls')) : null;
    const isTrigger = target =>
      Object.values(triggerRefs.current).some(node => node && node.contains(target));
    const handleClick = event => {
      const inPopover = popover && popover.contains(event.target);
      if (!inPopover && !isTrigger(event.target)) {
        setOpenKey(null);
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setOpenKey(null);
        if (trigger) {
          trigger.focus();
        }
      }
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openKey]);
  const toggleGroup = categoryId => {
    const isClosing = openGroups.has(categoryId);
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (isClosing) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
    // A popover left open inside a group that is being collapsed would stay open while hidden.
    if (isClosing && openKey && openKey.includes(categoryId)) {
      setOpenKey(null);
    }
  };

  // Nothing to render if the category asset has not loaded / is empty.
  if (categories.length === 0) {
    return null;
  }

  return (
    <SectionContainer id={sectionId} className={classNames(className, css.sectionDark)}>
      {/* Stable hash-scroll target. The SectionContainer's own id comes from the
          Console sectionId, so this local anchor guarantees a #all-categories target. */}
      <div className={css.outer} id="all-categories">
        <div className={css.panel}>
          <header className={css.intro}>
            <h2 className={classNames('delowo-text--section-title', css.heading)}>
              <FormattedMessage id="SectionCategoryDirectory.heading" />
            </h2>
          </header>

          <div className={css.directoryInteractionArea}>
            <div className={css.directoryGrid}>
              {displayCategories.map(category => {
                // Direct subcategories only. We intentionally do NOT read
                // subcategory.subcategories, so level 3+ is never rendered.
                const subcategories = category.subcategories ?? [];
                const iconUrl = getCategoryIconUrl(category.id);

                const catKey = `cat:${category.id}`;
                const catOpen = openKey === catKey;
                const catPopoverId = `category-actions-${category.id}`;
                const groupOpen = openGroups.has(category.id);
                const groupListId = `category-group-${category.id}`;
                const categorySearch = topLevelServiceSearch(category.id, serviceTypes);
                const categoryPopover = catOpen ? (
                  <CategoryActionPopover
                    id={catPopoverId}
                    label={category.name}
                    role={role}
                    taskSearch={topLevelTaskSearch(category.id)}
                    serviceSearch={categorySearch}
                    findTaskSearch={categoryTaskSearch(category.id)}
                  />
                ) : null;
                const categoryActionsButton = (
                  <CategoryActionsButton
                    name={category.name}
                    isOpen={catOpen}
                    popoverId={catPopoverId}
                    buttonRef={node => {
                      triggerRefs.current[catKey] = node;
                    }}
                    onClick={() => toggleKey(catKey)}
                  />
                );
                const categoryIcon = iconUrl ? (
                  // A1-09: size attributes (CSS 32 / 28px still wins); below the fold → lazy
                  <img
                    src={iconUrl}
                    alt=""
                    aria-hidden="true"
                    className={css.groupIcon}
                    width="32"
                    height="32"
                    loading="lazy"
                  />
                ) : null;

                return (
                  <div key={category.id} className={css.group}>
                    <h3 className={css.groupHeading}>
                      {isMobile ? (
                        // Mobile: the heading expands the group; the link and the «+» button
                        // are in the «Вся категория» row inside it.
                        <button
                          type="button"
                          className={classNames(css.groupTrigger, {
                            [css.groupTriggerOpen]: groupOpen,
                          })}
                          aria-expanded={groupOpen}
                          aria-controls={groupListId}
                          onClick={() => toggleGroup(category.id)}
                        >
                          {categoryIcon}
                          <span className={css.groupTriggerText}>{category.name}</span>
                          <span className={css.groupChevron} aria-hidden="true">
                            <IconArrowHead direction="down" size="small" />
                          </span>
                        </button>
                      ) : (
                        <span className={css.popoverAnchor}>
                          <span className={css.itemRow}>
                            <NamedLink
                              name="SearchPage"
                              to={{ search: categorySearch }}
                              className={css.groupLink}
                            >
                              {categoryIcon}
                              <span className={css.groupTriggerText}>{category.name}</span>
                            </NamedLink>
                            {categoryActionsButton}
                          </span>
                          {categoryPopover}
                        </span>
                      )}
                    </h3>

                    {/* All names stay in the server-rendered HTML; on mobile a collapsed
                        group's list is hidden by CSS (.subListCollapsed), not unmounted. */}
                    {subcategories.length > 0 || isMobile ? (
                      <ul
                        id={groupListId}
                        className={classNames(css.subList, {
                          [css.subListCollapsed]: !groupOpen,
                        })}
                      >
                        {isMobile ? (
                          <li className={css.subItem}>
                            <span className={css.popoverAnchor}>
                              <span className={css.itemRow}>
                                <NamedLink
                                  name="SearchPage"
                                  to={{ search: categorySearch }}
                                  className={css.subLink}
                                >
                                  <span className={css.subTriggerText}>
                                    <FormattedMessage id="SectionCategoryDirectory.allInCategory" />
                                  </span>
                                </NamedLink>
                                {categoryActionsButton}
                              </span>
                              {categoryPopover}
                            </span>
                          </li>
                        ) : null}
                        {subcategories.map(subcategory => {
                          const subKey = `sub:${category.id}:${subcategory.id}`;
                          const subOpen = openKey === subKey;
                          const subPopoverId = `subcategory-actions-${category.id}-${subcategory.id}`;

                          return (
                            <li key={subcategory.id} className={css.subItem}>
                              <span className={css.popoverAnchor}>
                                <span className={css.itemRow}>
                                  <NamedLink
                                    name="SearchPage"
                                    to={{
                                      search: subcategoryServiceSearch(
                                        category.id,
                                        subcategory.id,
                                        serviceTypes
                                      ),
                                    }}
                                    className={css.subLink}
                                  >
                                    <span className={css.subTriggerText}>{subcategory.name}</span>
                                  </NamedLink>
                                  <CategoryActionsButton
                                    name={subcategory.name}
                                    isOpen={subOpen}
                                    popoverId={subPopoverId}
                                    buttonRef={node => {
                                      triggerRefs.current[subKey] = node;
                                    }}
                                    onClick={() => toggleKey(subKey)}
                                  />
                                </span>

                                {subOpen ? (
                                  <CategoryActionPopover
                                    id={subPopoverId}
                                    label={subcategory.name}
                                    role={role}
                                    taskSearch={subcategoryTaskSearch(category.id, subcategory.id)}
                                    serviceSearch={subcategoryServiceSearch(
                                      category.id,
                                      subcategory.id,
                                      serviceTypes
                                    )}
                                    findTaskSearch={categoryTaskSearch(category.id, subcategory.id)}
                                  />
                                ) : null}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionCategoryDirectory;
